import { isMobile, osName, browserName } from "react-device-detect";
import { getEdgeTelemetryUrl } from "./edge";

/**
 * Fire-and-forget capture-funnel telemetry.
 *
 * Module-level singleton (not a hook) because some emitters run outside React
 * entirely — e.g. preloadFaceDetectionModels() fires at module scope. Events
 * are beaconed one-at-a-time to the queue-publisher /telemetry endpoint, which
 * stamps geo server-side and never blocks: failures are swallowed, delivery is
 * best-effort by design.
 *
 * Events emitted before the session context is known (early model preload) are
 * buffered with their original timestamps and flushed on setTelemetryContext.
 */

const context = {
  request_id: null,
  site_name: null,
  variant: null, // capture variant: 'optimized' | 'legacy'
  journey_name: null,
  route_app: null,
  route_path: null,
  step_index: null,
  total_steps: null,
};

const device = {
  device_type: isMobile ? "mobile" : "desktop",
  os_name: osName,
  browser_name: browserName,
};

// Which end of a QR device handoff this page is. The handoff receiver
// loads with `qr=true` (set by the Handoff / CompactHandoff QR URL), so
// a session that hands off produces two page contexts — both emit
// landed / verification_success — and this tag keeps them apart
// without suppressing either side of the funnel.
const deviceRole = (() => {
  try {
    return new URLSearchParams(window.location.search).get("qr") === "true"
      ? "handoff"
      : "original";
  } catch {
    return "original";
  }
})();

let seq = 0;
const onceSent = new Set();
const preContextBuffer = [];
const PRE_CONTEXT_BUFFER_MAX = 50;

// Journey-scoped enablement: telemetry is opt-in per journey. null =
// undecided (events buffer), true = send, false = drop everything.
// DataContext decides once the routeSequence is known — currently only
// journeys containing a face-capture/age-estimation step are enabled.
let enabled = null;

// Events that beacon before the session context is complete (e.g. the
// step's `landed`, fired on mount before settings sync site_name) would
// otherwise land with site_name null and bucket under "(unknown)" in
// reporting. Hold the buffer until site_name is known — with a timer
// fallback so a session that never learns its site still delivers
// (with null site) instead of silently losing its events.
let contextWaitExpired = false;
let contextWaitTimer = null;
const armContextWaitTimer = () => {
  if (contextWaitTimer || contextWaitExpired) return;
  try {
    contextWaitTimer = setTimeout(() => {
      contextWaitExpired = true;
      maybeFlush();
    }, 10000);
  } catch {
    contextWaitExpired = true;
  }
};

const maybeFlush = () => {
  if (enabled === true && context.request_id && (context.site_name || contextWaitExpired) && preContextBuffer.length > 0) {
    const buffered = preContextBuffer.splice(0, preContextBuffer.length);
    buffered.forEach((payload) => send({ ...payload, ...context, ...device }));
  }
};

export const setTelemetryEnabled = (value) => {
  try {
    enabled = value === true;
    if (!enabled) {
      preContextBuffer.length = 0;
      return;
    }
    maybeFlush();
  } catch {
    // never throw
  }
};

const send = (payload) => {
  try {
    const url = getEdgeTelemetryUrl();
    const json = JSON.stringify(payload);

    // Primary path: sendBeacon — survives component teardown / page unload.
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        const blob = new Blob([json], { type: "application/json" });
        if (navigator.sendBeacon(url, blob)) return;
      } catch {
        // fall through to fetch
      }
    }

    fetch(url, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: json,
    }).catch(() => {});
  } catch {
    // Telemetry must never throw into the app
  }
};

/**
 * Merge session/journey context and flush any events buffered before the
 * session was known. Called from DataContext when settings arrive and when
 * the journey advances to a new stop.
 */
export const setTelemetryContext = (next = {}) => {
  try {
    Object.keys(next).forEach((key) => {
      if (key in context && next[key] !== undefined && next[key] !== null) {
        context[key] = next[key];
      }
    });
    maybeFlush();
  } catch {
    // never throw
  }
};

/**
 * Emit one funnel milestone. `once: true` dedupes per page load (e.g. the
 * detection loop remounting must not re-emit detection_loop_started).
 */
/**
 * Experiment arm for the active session (currently the face inference
 * engine). Stamped into every event's metadata the same way device_role
 * is, so an arm split needs no join back to models_ready and the
 * `variant` column stays free for whatever the dashboards already use.
 */
let experimentArm = null;

export const setExperimentArm = (arm) => {
  try {
    if (arm) experimentArm = arm;
  } catch {
    // never throw
  }
};

export const trackEvent = (name, props = {}, { once = false } = {}) => {
  try {
    if (enabled === false) return;
    if (once) {
      if (onceSent.has(name)) return;
      onceSent.add(name);
    }
    const payload = {
      ...context,
      ...device,
      event: name,
      sequence: ++seq,
      client_timestamp: new Date().toISOString(),
      elapsed_ms: Math.round(performance.now()),
      metadata: {
        device_role: deviceRole,
        ...(experimentArm ? { engine: experimentArm } : {}),
        ...props,
      },
      error: props?.error !== undefined ? String(props.error) : undefined,
    };
    if (!context.request_id || !context.site_name || enabled !== true) {
      if (preContextBuffer.length < PRE_CONTEXT_BUFFER_MAX) {
        preContextBuffer.push(payload);
        armContextWaitTimer();
      }
      return;
    }
    send(payload);
  } catch {
    // Telemetry must never throw into the app
  }
};
